import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiTruck, FiTrash2 } from 'react-icons/fi';
import { veiculosAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import ImageUploader from '../components/ImageUploader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    gap: ${theme.spacing[4]};
    width: 100%;
    max-width: 100vw;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};

  @media (max-width: 768px) {
    gap: ${theme.spacing[2]};
    margin-bottom: ${theme.spacing[2]};
  }
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: ${theme.spacing[3]};
    width: 100%;
    max-width: 100vw;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
    margin-bottom: ${theme.spacing[4]};
    width: 100%;
    max-width: 100%;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Previne zoom no iOS */
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  cursor: pointer;
  min-height: 48px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.muted};
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Previne zoom no iOS */
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    min-height: 44px;
  }
`;

const LoadingText = styled.small`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing[1]};
  display: block;
`;

const AcessoriosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const SectionSpacer = styled.div`
  margin: ${theme.spacing[6]} 0;

  @media (max-width: 768px) {
    margin: ${theme.spacing[4]} 0;
  }
`;

const AcessoriosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: ${theme.spacing[2]};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing[2]};
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const AcessorioButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$selected ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${props => props.$selected ? theme.colors.text.primary : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.primary[50]};
    color: ${props => props.$selected ? theme.colors.text.primary : theme.colors.primary[600]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    min-height: 44px;
    font-size: ${theme.fontSizes.xs};
  }

  @media (max-width: 480px) {
    min-height: 40px;
    padding: ${theme.spacing[2]};
  }
`;

const PlaceholderButton = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 2px solid transparent;
  border-radius: ${theme.borderRadius.lg};
  min-height: 48px;
  visibility: hidden;
  pointer-events: none;
`;

// Container e controles do carrossel de acessórios
const AcessoriosCarousel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageIndicator = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`;

// Viewport e trilho para carrossel com "peek" da próxima página
const AcessoriosViewport = styled.div`
  position: relative;
  overflow: visible; /* Permite que os botões fiquem visíveis nas bordas */
  padding: 12px 50px; /* Espaço vertical aumentado para não cortar primeira linha e horizontal para botões */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px 40px;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 12px 35px;
    width: 100%;
    max-width: 100%;
  }
`;

const AcessoriosTrack = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  scroll-behavior: smooth;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  padding: 0 40px; /* Espaço para mostrar 25% de um card de cada lado */
  width: 100%;
  box-sizing: border-box;
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  @media (max-width: 768px) {
    padding: 0 30px;
    gap: ${theme.spacing[2]};
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0 25px;
    width: 100%;
    max-width: 100%;
  }
`;

const PageGrid = styled.div`
  flex: 0 0 calc(100% - 80px); /* Largura menor para mostrar apenas 25% de um card de cada lado */
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: ${theme.spacing[2]};
  transition: all 0.3s ease;
  transform: ${props => props.$isCurrentPage ? 'scale(1.02)' : 'scale(1)'};
  z-index: ${props => props.$isCurrentPage ? '5' : '1'};
  position: relative;
  margin: ${props => props.$isCurrentPage ? `0 ${theme.spacing[4]}` : '0'}; /* Espaço entre página atual e adjacentes */
  box-sizing: border-box;
  min-width: 0; /* Permite que o grid se ajuste */
  
  /* Destaque para página atual - trazendo para frente sem cores */
  ${props => props.$isCurrentPage && `
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    filter: brightness(1.05);
  `}
  
  /* Páginas não atuais - um pouco mais para trás */
  ${props => !props.$isCurrentPage && `
    opacity: 0.85;
    filter: brightness(0.95);
  `}

  @media (max-width: 768px) {
    flex: 0 0 calc(100% - 60px);
    grid-template-columns: repeat(2, 1fr);
    margin: ${props => props.$isCurrentPage ? `0 ${theme.spacing[2]}` : '0'};
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex: 0 0 calc(100% - 40px);
    grid-template-columns: 1fr;
    margin: ${props => props.$isCurrentPage ? `0 ${theme.spacing[2]}` : '0'};
    min-width: 0;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary[500]};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  font-size: 24px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  left: ${props => props.$left !== undefined ? `${props.$left}px` : 'auto'};
  right: ${props => props.$right !== undefined ? `${props.$right}px` : 'auto'};
  
  &:hover:not(:disabled) {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.text.primary};
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    border-color: ${theme.colors.border.primary};
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 20px;
    left: ${props => props.$left !== undefined ? `${Math.max(8, props.$left - 8)}px` : 'auto'};
    right: ${props => props.$right !== undefined ? `${Math.max(8, props.$right - 8)}px` : 'auto'};
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 18px;
    left: ${props => props.$left !== undefined ? `${Math.max(4, props.$left - 12)}px` : 'auto'};
    right: ${props => props.$right !== undefined ? `${Math.max(4, props.$right - 12)}px` : 'auto'};
  }
`;

const AcessoriosPageIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[2]} 0;
`;

const AcessoriosPageDot = styled.div`
  width: ${props => props.$active ? '10px' : '8px'};
  height: ${props => props.$active ? '10px' : '8px'};
  border-radius: 50%;
  background: ${props => props.$active ? theme.colors.primary[500] : theme.colors.text.muted};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.$active ? theme.colors.primary[600] : theme.colors.text.secondary};
    transform: scale(1.2);
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  min-height: 100px;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Previne zoom no iOS */
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    min-height: 80px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary[500]};
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[2]};
  }
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$success) return theme.colors.success[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$success) return theme.colors.success[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$success) return theme.colors.text.primary;
    if (props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$success) return theme.colors.success[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$success) return theme.colors.text.primary;
      if (props.$primary) return theme.colors.text.primary;
      return theme.colors.error[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$success) return theme.colors.success[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.error[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    font-size: ${theme.fontSizes.sm};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[50]};
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.error[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  
  &::before {
    content: '⚠️';
    font-size: ${theme.fontSizes.lg};
  }
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: ${theme.spacing[4]};
    width: 95%;
    max-width: none;
  }
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[2]};
  }
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.primary};
    color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.error[500]};
    border-color: ${props => props.$danger ? theme.colors.error[600] : theme.colors.error[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    font-size: ${theme.fontSizes.sm};
  }
`;

const VeiculoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const veiculoId = id; // Para usar no ImageUploader
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para API FIPE
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [anos, setAnos] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [loadingAnos, setLoadingAnos] = useState(false);
  
  // Estados para acessórios
  const [acessorios, setAcessorios] = useState([]);
  const [loadingAcessorios, setLoadingAcessorios] = useState(false);
  const [acessoriosPage, setAcessoriosPage] = useState(0);
  const acessoriosViewportRef = useRef(null);
  const acessoriosTrackRef = useRef(null);
  // Estado para manter a ordem inicial estável (não muda quando seleção muda)
  const [acessoriosOrdenadosInicial, setAcessoriosOrdenadosInicial] = useState([]);
  
  // Estados para clientes
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  
  // Estados para imagens
  const [imagens, setImagens] = useState([]);
  const [imagensPendentes, setImagensPendentes] = useState([]); // Imagens aguardando upload após criação do veículo
  
  // Estados para exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    placa: '',
    marca: '',
    modelo: '',
    cor: '',
    ano_modelo: '',
    // Campos para códigos FIPE
    codigo_marca: '',
    codigo_modelo: '',
    codigo_ano: '',
    descricao: '',
    valor: '',
    financiamento: false,
    taxa_juros: '',
    troca: false,
    descricao_troca: '',
    em_estoque: true,
    acessorios: [],
    code: '',
    potencia: '',
    motor: '',
    tipo_veiculo: '',
    veiculo: 'Carro',
    km: '',
    id_cliente: null,
    combustivel: ''
  });

  // Carregar dados do veículo se estiver editando
  useEffect(() => {
    if (isEditing && id) {
      carregarVeiculo();
    }
  }, [id, isEditing]);

  // Carregar marcas da FIPE
  useEffect(() => {
    buscarMarcas();
  }, []);

  // Carregar acessórios
  useEffect(() => {
    buscarAcessorios();
  }, []);

  // Atualizar ordem inicial quando veículo for carregado em modo de edição
  useEffect(() => {
    if (isEditing && acessorios.length > 0 && formData.acessorios && formData.acessorios.length > 0 && acessoriosOrdenadosInicial.length === 0) {
      const selectedSet = new Set(formData.acessorios);
      const ordenados = [...acessorios].sort((a, b) => {
        const sa = selectedSet.has(a.id);
        const sb = selectedSet.has(b.id);
        if (sa === sb) return 0;
        return sa ? -1 : 1;
      });
      setAcessoriosOrdenadosInicial(ordenados);
    } else if (!isEditing && acessorios.length > 0 && acessoriosOrdenadosInicial.length === 0) {
      // Em modo criação, manter ordem original
      setAcessoriosOrdenadosInicial(acessorios);
    }
  }, [isEditing, acessorios, formData.acessorios]);

  // Hook para detectar tamanho da tela
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth <= 768;
      setIsMobile(nowMobile);
      
      // Resetar página quando mudar entre mobile e desktop
      if (wasMobile !== nowMobile) {
        setAcessoriosPage(0);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Sincronizar scroll do carrossel quando página muda
  useLayoutEffect(() => {
    const track = acessoriosTrackRef.current;
    const viewport = acessoriosViewportRef.current;
    if (!track || !viewport || acessoriosOrdenadosInicial.length === 0) return;

    // Em mobile: 7 itens por página (1 coluna x 7 linhas)
    // Em desktop: 12 itens por página (4 colunas x 3 linhas)
    const ITEMS_PER_PAGE = isMobile ? 7 : 12;
    const totalPages = Math.max(1, Math.ceil(acessoriosOrdenadosInicial.length / ITEMS_PER_PAGE));
    const page = Math.min(acessoriosPage, totalPages - 1);

    // Aguardar próximo frame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      const firstPageGrid = track.children[0];
      if (firstPageGrid) {
        const gap = parseInt(getComputedStyle(track).gap) || 12;
        const peekWidth = 40; // 25% de um card para mostrar em cada lado
        const marginSpacing = 16; // theme.spacing[4] - margem horizontal da página atual
        
        // Calcular scroll acumulado
        let target = 0;
        if (page > 0) {
          // Somar todas as páginas anteriores incluindo suas margens e gaps
          for (let i = 0; i < page; i++) {
            const prevGrid = track.children[i];
            if (prevGrid) {
              const prevWidth = prevGrid.offsetWidth;
              const prevMargin = parseInt(getComputedStyle(prevGrid).marginLeft) || 0;
              const prevMarginRight = parseInt(getComputedStyle(prevGrid).marginRight) || 0;
              target += prevWidth + (prevMargin + prevMarginRight);
              // Adicionar gap após cada página (exceto a última página anterior)
              if (i < page - 1) {
                target += gap;
              }
            }
          }
          // Ajustar para mostrar 25% do anterior
          target -= peekWidth;
        }
        
        track.scrollLeft = Math.max(0, target);
      }
    });
  }, [acessoriosPage, acessoriosOrdenadosInicial.length, isMobile]);

  // Carregar clientes e aplicar automaticamente se tiver apenas 1
  useEffect(() => {
    const carregarClientesEAplicarFiltro = async () => {
      try {
        const response = await veiculosAPI.listarClientes();
        if (response.data.success) {
          const clientesCarregados = response.data.data || [];
          setClientes(clientesCarregados);

          if (
            !isEditing &&
            clientesCarregados.length === 1 &&
            clientesCarregados[0]?.id != null
          ) {
            setFormData(prevData => ({
              ...prevData,
              id_cliente: clientesCarregados[0].id
            }));
          }
        } else {
          setClientes([]);
        }
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        setClientes([]);
      }
    };

    carregarClientesEAplicarFiltro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gerar código automaticamente quando campos FIPE forem preenchidos
  useEffect(() => {
    if (!isEditing && formData.marca && formData.ano_modelo) {
      atualizarCodigoAutomaticamente();
    }
  }, [formData.marca, formData.modelo, formData.ano_modelo, formData.codigo_modelo, isEditing]);

  const carregarVeiculo = async () => {
    try {
      setLoading(true);
      const response = await veiculosAPI.buscarPorId(id);
      
      if (response.data.success) {
        const veiculo = response.data.data;
        setFormData({
          placa: veiculo.placa || '',
          marca: veiculo.marca || '',
          modelo: veiculo.modelo || '',
          cor: veiculo.cor || '',
          ano_modelo: veiculo.ano_modelo || '',
          // Campos para códigos FIPE
          codigo_marca: veiculo.codigo_marca || '',
          codigo_modelo: veiculo.codigo_modelo || '',
          codigo_ano: veiculo.codigo_ano || '',
          descricao: veiculo.descricao || '',
          valor: veiculo.valor ? aplicarFormatacaoMoeda((veiculo.valor * 100).toString()) : '',
          financiamento: veiculo.financiamento || false,
          taxa_juros: veiculo.taxa_juros || '',
          troca: veiculo.troca || false,
          descricao_troca: veiculo.descricao_troca || '',
          em_estoque: veiculo.em_estoque !== undefined ? veiculo.em_estoque : true,
          acessorios: Array.isArray(veiculo.acessorios) ? veiculo.acessorios : (typeof veiculo.acessorios === 'string' ? JSON.parse(veiculo.acessorios || '[]') : []),
          code: veiculo.code || '',
          potencia: veiculo.potencia || '',
          motor: veiculo.motor !== null && veiculo.motor !== undefined ? aplicarFormatacaoMotor(veiculo.motor) : '',
          tipo_veiculo: veiculo.tipo_veiculo || '',
          veiculo: veiculo.veiculo || 'Carro',
          km: veiculo.km ? aplicarFormatacaoKm(veiculo.km.toString()) : '',
          id_cliente: veiculo.id_cliente || null,
          combustivel: veiculo.combustivel || ''
        });

        // Carregar imagens existentes do veículo
        if (veiculo.imagens && Array.isArray(veiculo.imagens) && veiculo.imagens.length > 0) {
          // Converter as imagens do formato do backend para o formato esperado pelo ImageUploader
          const imagensExistentes = veiculo.imagens.map(img => ({
            id: img.id,
            preview: img.link, // Usar 'preview' em vez de 'url' para compatibilidade com ImageUploader
            name: `imagem_${img.id}`,
            size: 0, // Não temos o tamanho no backend
            type: 'image/jpeg', // Assumir JPEG por padrão
            favorita: img.favorita || false // Incluir campo favorita
          }));
          setImagens(imagensExistentes);
        } else {
          setImagens([]);
        }
      } else {
        setError('Veículo não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar veículo:', err);
      setError('Erro ao carregar dados do veículo');
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar código único
  const gerarCodigoUnico = async (marca, modelo, ano) => {
    if (!marca || !ano) {
      return '';
    }

    // Se modelo estiver vazio, usar código do modelo como fallback
    const modeloParaCodigo = modelo || formData.codigo_modelo || 'MOD';
    
    // Criar base do código: primeiras 3 letras da marca + primeiras 3 letras do modelo + ano
    const marcaAbrev = marca.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '');
    const modeloAbrev = modeloParaCodigo.substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '');
    const anoAbrev = ano.toString().substring(2); // Últimos 2 dígitos do ano
    
    let codigoBase = `${marcaAbrev}${modeloAbrev}${anoAbrev}`;
    
    // Garantir que não exceda 12 caracteres para deixar espaço para contador
    if (codigoBase.length > 12) {
      codigoBase = codigoBase.substring(0, 12);
    }

    // Verificar se o código já existe e gerar variações se necessário
    let codigoFinal = codigoBase;
    let contador = 1;
    
    try {
      // Buscar veículos existentes para verificar unicidade
      const response = await veiculosAPI.listar({ limit: 1000 }); // Buscar todos para verificar códigos
      
      if (response.data.success) {
        const veiculosExistentes = response.data.data || [];
        const codigosExistentes = veiculosExistentes.map(v => v.code).filter(Boolean);
        
        // Se estiver editando, excluir o código atual do veículo
        if (isEditing && id) {
          const index = codigosExistentes.indexOf(formData.code);
          if (index > -1) {
            codigosExistentes.splice(index, 1);
          }
        }
        
        // Encontrar código único
        while (codigosExistentes.includes(codigoFinal)) {
          const sufixo = contador.toString().padStart(2, '0');
          codigoFinal = `${codigoBase}${sufixo}`;
          contador++;
          
          // Se exceder 15 caracteres, truncar
          if (codigoFinal.length > 15) {
            codigoFinal = codigoFinal.substring(0, 15);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao verificar códigos existentes:', error);
      // Em caso de erro, usar código base com timestamp
      const timestamp = Date.now().toString().slice(-3);
      codigoFinal = `${codigoBase}${timestamp}`;
    }

    return codigoFinal;
  };

  // Função para atualizar código automaticamente
  const atualizarCodigoAutomaticamente = async () => {
    if (!isEditing && formData.marca && formData.ano_modelo) {
      const novoCodigo = await gerarCodigoUnico(formData.marca, formData.modelo, formData.ano_modelo);
      if (novoCodigo) {
        setFormData(prev => ({
          ...prev,
          code: novoCodigo
        }));
      }
    }
  };

  // Funções para API FIPE
  const buscarMarcas = async () => {
    try {
      setLoadingMarcas(true);
      const response = await fetch('https://parallelum.com.br/fipe/api/v1/carros/marcas/');
      const data = await response.json();
      setMarcas(data);
    } catch (error) {
      console.error('Erro ao buscar marcas:', error);
      setError('Erro ao carregar marcas');
    } finally {
      setLoadingMarcas(false);
    }
  };

  // Função para buscar acessórios
  const buscarAcessorios = async () => {
    try {
      setLoadingAcessorios(true);
      const response = await veiculosAPI.listarAcessorios();
      if (response.data.success) {
        setAcessorios(response.data.data || []);
      } else {
        setAcessorios([]);
      }
    } catch (error) {
      console.error('Erro ao buscar acessórios:', error);
      setError('Erro ao carregar acessórios');
      setAcessorios([]);
    } finally {
      setLoadingAcessorios(false);
    }
  };

  // Função para buscar clientes
  const buscarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await veiculosAPI.listarClientes();
      if (response.data.success) {
        const clientesCarregados = response.data.data || [];
        setClientes(clientesCarregados);

        if (
          !isEditing &&
          clientesCarregados.length === 1 &&
          clientesCarregados[0]?.id != null
        ) {
          setFormData(prevData => ({
            ...prevData,
            id_cliente: clientesCarregados[0].id
          }));
        }
      } else {
        setClientes([]);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setError('Erro ao carregar clientes');
      setClientes([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  const buscarModelos = async (codigoMarca) => {
    if (!codigoMarca) {
      setModelos([]);
      return;
    }
    
    try {
      setLoadingModelos(true);
      const response = await fetch(`https://parallelum.com.br/fipe/api/v1/carros/marcas/${codigoMarca}/modelos`);
      const data = await response.json();
      
      setModelos(data.modelos || []);
    } catch (error) {
      console.error('Erro ao buscar modelos:', error);
      setError('Erro ao carregar modelos');
    } finally {
      setLoadingModelos(false);
    }
  };

  const buscarAnos = async (codigoMarca, codigoModelo) => {
    if (!codigoMarca || !codigoModelo) {
      setAnos([]);
      return;
    }
    
    try {
      setLoadingAnos(true);
      const response = await fetch(`https://parallelum.com.br/fipe/api/v1/carros/marcas/${codigoMarca}/modelos/${codigoModelo}/anos`);
      const data = await response.json();
      
      // Filtrar anos entre 1900 e ano atual + 2
      const anoAtual = new Date().getFullYear();
      const anosFiltrados = data.filter(ano => {
        const anoNumero = parseInt(ano.nome.split(' ')[0]);
        return anoNumero >= 1900 && anoNumero <= (anoAtual + 2);
      });
      
      setAnos(anosFiltrados);
    } catch (error) {
      console.error('Erro ao buscar anos:', error);
      setError('Erro ao carregar anos');
    } finally {
      setLoadingAnos(false);
    }
  };

  // Função para aplicar máscara de placa
  const aplicarMascaraPlaca = (valor) => {
    // Remove tudo que não é letra ou número
    const apenasLetrasNumeros = valor.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    // Modelo antigo: ABC-1234 (3 letras + 4 números)
    if (apenasLetrasNumeros.length <= 7) {
      if (apenasLetrasNumeros.length <= 3) {
        return apenasLetrasNumeros;
      } else {
        return apenasLetrasNumeros.slice(0, 3) + '-' + apenasLetrasNumeros.slice(3, 7);
      }
    }
    
    // Modelo novo: ABC1D23 (3 letras + 1 número + 1 letra + 2 números)
    if (apenasLetrasNumeros.length > 7) {
      return apenasLetrasNumeros.slice(0, 3) + 
             apenasLetrasNumeros.slice(3, 4) + 
             apenasLetrasNumeros.slice(4, 5) + 
             apenasLetrasNumeros.slice(5, 7);
    }
    
    return apenasLetrasNumeros;
  };

  // Função para aplicar formatação de quilometragem
  const aplicarFormatacaoKm = (valor) => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/[^0-9]/g, '');
    
    if (!apenasNumeros) return '';
    
    // Converte para número e aplica formatação brasileira
    const numero = parseInt(apenasNumeros);
    return new Intl.NumberFormat('pt-BR').format(numero);
  };

  // Função para aplicar formatação de moeda brasileira
  const aplicarFormatacaoMoeda = (valor) => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/[^0-9]/g, '');
    
    if (!apenasNumeros) return '';
    
    // Converte para número e divide por 100 para considerar centavos
    const numero = parseInt(apenasNumeros) / 100;
    
    // Aplica formatação de moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numero);
  };

  // Formatação para o campo motor: quando o valor for inteiro, exibir uma casa decimal (ex.: "2" -> "2.0")
  const aplicarFormatacaoMotor = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';

    let valorStr = String(valor).trim();
    if (!valorStr || valorStr === '') return '';

    // Normaliza vírgula para ponto para facilitar o parse
    valorStr = valorStr.replace(',', '.');

    // Se o usuário já digitou um ponto decimal, mantém como está
    if (valorStr.includes('.')) {
      const numero = parseFloat(valorStr);
      if (Number.isNaN(numero)) return '';
      // Se já tem decimal, retorna formatado com até 1 casa decimal
      return numero.toFixed(1);
    }

    const numero = parseFloat(valorStr);
    if (Number.isNaN(numero)) return '';

    // Para inteiros, garante uma casa decimal (2 => 2.0)
    if (Number.isInteger(numero)) {
      return numero.toFixed(1);
    }

    return valorStr;
  };

  const handleInputChange = (field, value) => {
    // Aplicar máscara especial para placa
    if (field === 'placa') {
      const valorComMascara = aplicarMascaraPlaca(value);
      setFormData(prev => ({
        ...prev,
        [field]: valorComMascara
      }));
    } else if (field === 'km') {
      // Aplicar formatação de quilometragem
      const valorFormatado = aplicarFormatacaoKm(value);
      setFormData(prev => ({
        ...prev,
        [field]: valorFormatado
      }));
    } else if (field === 'valor') {
      // Aplicar formatação de moeda
      const valorFormatado = aplicarFormatacaoMoeda(value);
      setFormData(prev => ({
        ...prev,
        [field]: valorFormatado
      }));
    } else {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    }
  };

  // Função para formatar o campo motor quando perder o foco
  const handleMotorBlur = (e) => {
    const valor = e.target.value;
    if (valor === '' || valor === null || valor === undefined) {
      // Se o campo estiver vazio, apenas limpar
      setFormData(prev => ({
        ...prev,
        motor: ''
      }));
      return;
    }
    
    // Aplicar formatação apenas quando o campo perder o foco
    const valorFormatado = aplicarFormatacaoMotor(valor);
    setFormData(prev => ({
      ...prev,
      motor: valorFormatado
    }));
  };

  // Função para lidar com seleção de acessórios
  const handleAcessorioToggle = (acessorioId) => {
    setFormData(prev => {
      const acessoriosAtuais = prev.acessorios || [];
      const acessorioExiste = acessoriosAtuais.includes(acessorioId);
      
      let novosAcessorios;
      if (acessorioExiste) {
        // Remover acessório
        novosAcessorios = acessoriosAtuais.filter(id => id !== acessorioId);
      } else {
        // Adicionar acessório
        novosAcessorios = [...acessoriosAtuais, acessorioId];
      }
      
      return {
        ...prev,
        acessorios: novosAcessorios
      };
    });
    // Não resetar página - manter página atual quando selecionar/deselecionar acessórios
  };


  // Funções específicas para campos FIPE
  const handleMarcaChange = (codigoMarca) => {
    const marcaSelecionada = marcas.find(marca => marca.codigo === codigoMarca);
    
    // Atualizar estado de forma mais eficiente
    setFormData(prev => ({
      ...prev,
      codigo_marca: codigoMarca,
      marca: marcaSelecionada ? marcaSelecionada.nome : '',
      // Limpar campos dependentes
      codigo_modelo: '',
      modelo: '',
      codigo_ano: '',
      ano_modelo: ''
    }));
    
    // Limpar modelos e anos imediatamente
    setModelos([]);
    setAnos([]);
    
    // Buscar modelos se não estiver editando
    if (!isEditing && codigoMarca) {
      buscarModelos(codigoMarca);
    }
  };

  const handleModeloChange = (codigoModelo) => {
    // Converter para número para comparação correta
    const codigoModeloNumero = parseInt(codigoModelo);
    const modeloSelecionado = modelos.find(modelo => modelo.codigo === codigoModeloNumero);
    
    // Atualizar estado de forma mais eficiente
    setFormData(prev => {
      return {
        ...prev,
        codigo_modelo: codigoModelo,
        modelo: modeloSelecionado ? modeloSelecionado.nome : '',
        // Limpar campo dependente
        codigo_ano: '',
        ano_modelo: ''
      };
    });
    
    // Limpar anos imediatamente
    setAnos([]);
    
    // Buscar anos se não estiver editando
    if (!isEditing && codigoModelo && formData.codigo_marca) {
      buscarAnos(formData.codigo_marca, codigoModelo);
    }
  };

  const handleAnoChange = async (codigoAno) => {
    // Converter para string para comparação correta (API FIPE retorna códigos como string)
    const codigoAnoString = codigoAno.toString();
    const anoSelecionado = anos.find(ano => ano.codigo === codigoAnoString);
    
    if (anoSelecionado) {
      const anoNumero = parseInt(anoSelecionado.nome.split(' ')[0]);
      
      setFormData(prev => ({
        ...prev,
        codigo_ano: codigoAno,
        ano_modelo: anoNumero.toString()
      }));
      
      // Gerar código automaticamente quando todos os campos FIPE estiverem preenchidos
      if (!isEditing && formData.marca && formData.modelo) {
        const novoCodigo = await gerarCodigoUnico(formData.marca, formData.modelo, anoNumero.toString());
        if (novoCodigo) {
          setFormData(prev => ({
            ...prev,
            codigo_ano: codigoAno,
            ano_modelo: anoNumero.toString(),
            code: novoCodigo
          }));
        }
      }
    }
  };

  // Função para fazer upload de imagens pendentes após criação do veículo
  const uploadImagensPendentes = async (veiculoId, imagensParaUpload, idClienteOverride) => {
    const uploadFormData = new FormData();
    
    // Adicionar arquivos ao FormData
    imagensParaUpload.forEach(imagem => {
      if (imagem.file) {
        uploadFormData.append('imagens', imagem.file);
      }
    });
    
    // Adicionar id_cliente (prioriza override, senão usa estado atual)
    const idClienteParaEnvio = idClienteOverride ?? formData.id_cliente;
    if (idClienteParaEnvio !== null && idClienteParaEnvio !== undefined) {
      uploadFormData.append('id_cliente', idClienteParaEnvio);
    }

    const response = await veiculosAPI.uploadImagens(veiculoId, uploadFormData);
    
    if (response.data.success) {
      // Limpar imagens pendentes após upload bem-sucedido
      setImagensPendentes([]);
    }
    
    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);

      // Validar id_cliente quando houver múltiplos clientes disponíveis
      if (clientes.length > 1 && !formData.id_cliente) {
        setError('Por favor, selecione um cliente');
        setSaving(false);
        return;
      }
      
      // Se não tem id_cliente mas usuário tem apenas 1 cliente, usar esse cliente
      let idClienteFinal = formData.id_cliente;
      if (!idClienteFinal && clientes.length === 1) {
        idClienteFinal = clientes[0].id;
      }

      // Preparar dados para envio
      const dados = {
        ...formData,
        valor: formData.valor ? parseFloat(formData.valor.replace(/[^\d,]/g, '').replace(',', '.')) : 0, // Remove símbolos de moeda e converte vírgula para ponto
        ano_modelo: formData.ano_modelo ? parseInt(formData.ano_modelo) : null,
        potencia: formData.potencia ? parseFloat(formData.potencia) : null,
        motor: formData.motor ? parseFloat(formData.motor) : null,
        km: formData.km ? parseFloat(formData.km.replace(/\./g, '')) : null, // Remove pontos da formatação
        id_cliente: idClienteFinal ? parseInt(idClienteFinal) : null
      };

      if (isEditing) {
        await veiculosAPI.atualizar(id, dados);
        navigate('/veiculos');
      } else {
        const response = await veiculosAPI.criar(dados);
        
        // Se há imagens pendentes, fazer upload após criação do veículo
        if (imagensPendentes.length > 0 && response.data.success) {
          const novoVeiculoId = response.data.data.uuid || response.data.data.id;
          
          try {
            await uploadImagensPendentes(novoVeiculoId, imagensPendentes, idClienteFinal);
          } catch (error) {
            // Continuar mesmo com erro no upload de imagens
            console.warn('Erro ao fazer upload de imagens (não crítico):', error);
          }
        }
        
        navigate('/veiculos');
      }
    } catch (err) {
      console.error('Erro ao salvar veículo:', err);
      
      // Extrair mensagem de erro mais detalhada
      let errorMessage = 'Erro ao salvar veículo';
      
      if (err.response) {
        // Erro com resposta do servidor
        const status = err.response.status;
        const data = err.response.data;
        
        // Mensagem de erro do servidor
        if (data?.message) {
          errorMessage = data.message;
        } else if (data?.error) {
          errorMessage = data.error;
        } else if (status === 400) {
          errorMessage = 'Dados inválidos. Verifique os campos obrigatórios.';
        } else if (status === 403) {
          errorMessage = 'Acesso negado. Você não tem permissão para realizar esta ação.';
        } else if (status === 401) {
          // Erro de autenticação - o interceptor já deve ter tratado, mas vamos mostrar mensagem
          errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
        } else if (status === 500) {
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
        } else {
          errorMessage = `Erro ao salvar veículo (${status})`;
        }
        
        // Log detalhado para debug
        console.error('Detalhes do erro:', {
          status,
          data,
          url: err.config?.url,
          method: err.config?.method
        });
      } else if (err.request) {
        // Erro de rede
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
        console.error('Erro de rede:', err.request);
      } else {
        // Outro tipo de erro
        errorMessage = err.message || 'Erro desconhecido ao salvar veículo';
        console.error('Erro:', err.message);
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/veiculos');
  };

  // Função para excluir veículo
  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);

      await veiculosAPI.deletar(id);
      
      // Redirecionar para lista de veículos
      navigate('/veiculos');
    } catch (err) {
      console.error('Erro ao excluir veículo:', err);
      setError('Erro ao excluir veículo');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Função para abrir modal de confirmação
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // Função para cancelar exclusão
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <Layout title={isEditing ? 'Editar Veículo' : 'Novo Veículo'}>
        <LoadingContainer>
          Carregando dados do veículo...
        </LoadingContainer>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Veículo' : 'Novo Veículo'}>
      <Container>
        <Header>
          <BackButton onClick={handleCancel}>
            <FiArrowLeft />
          </BackButton>
        </Header>

        <Form onSubmit={handleSubmit}>
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        <FormGrid>
          <FormGroup>
            <Label>Placa *</Label>
            <Input
              type="text"
              value={formData.placa}
              onChange={(e) => handleInputChange('placa', e.target.value)}
              disabled={isEditing}
              style={isEditing ? {
                backgroundColor: '#f8f9fa',
                color: '#212529',
                cursor: 'not-allowed',
                border: '1px solid #dee2e6',
                opacity: 1
              } : {}}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Marca *</Label>
            {isEditing ? (
            <Input
                value={formData.marca || ''}
                disabled={true}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'not-allowed',
                  border: '1px solid #dee2e6',
                  opacity: 1
                }}
              />
            ) : (
              <>
                <Select
                  value={formData.codigo_marca}
                  onChange={(e) => handleMarcaChange(e.target.value)}
                  required
                >
                  <option value="">Selecione uma marca</option>
                  {marcas.map((marca) => (
                    <option key={marca.codigo} value={marca.codigo}>
                      {marca.nome}
                    </option>
                  ))}
                </Select>
                {loadingMarcas && <LoadingText>Carregando marcas...</LoadingText>}
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Modelo *</Label>
            {isEditing ? (
            <Input
                value={formData.modelo || ''}
                disabled={true}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'not-allowed',
                  border: '1px solid #dee2e6',
                  opacity: 1
                }}
              />
            ) : (
              <>
                <Select
                  value={formData.codigo_modelo}
                  onChange={(e) => handleModeloChange(e.target.value)}
                  disabled={!formData.codigo_marca}
                  required
                >
                  <option value="">Selecione um modelo</option>
                  {modelos.map((modelo) => (
                    <option key={modelo.codigo} value={modelo.codigo}>
                      {modelo.nome}
                    </option>
                  ))}
                </Select>
                {loadingModelos && <LoadingText>Carregando modelos...</LoadingText>}
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Ano</Label>
            {isEditing ? (
            <Input
                value={formData.ano_modelo || ''}
                disabled={true}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'not-allowed',
                  border: '1px solid #dee2e6',
                  opacity: 1
                }}
              />
            ) : (
              <>
                <Select
                  value={formData.codigo_ano}
                  onChange={(e) => handleAnoChange(e.target.value)}
                  disabled={!formData.codigo_modelo}
                >
                  <option value="">Selecione um ano</option>
                  {anos.map((ano) => (
                    <option key={ano.codigo} value={ano.codigo}>
                      {ano.nome.split(' ')[0]}
                    </option>
                  ))}
                </Select>
                {loadingAnos && <LoadingText>Carregando anos...</LoadingText>}
              </>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Cor</Label>
            <Select
              value={formData.cor}
              onChange={(e) => handleInputChange('cor', e.target.value)}
            >
              <option value="">Selecione a cor</option>
              <option value="Branco">Branco</option>
              <option value="Preto">Preto</option>
              <option value="Prata">Prata</option>
              <option value="Cinza">Cinza</option>
              <option value="Azul">Azul</option>
              <option value="Vermelho">Vermelho</option>
              <option value="Verde">Verde</option>
              <option value="Amarelo">Amarelo</option>
              <option value="Laranja">Laranja</option>
              <option value="Marrom">Marrom</option>
              <option value="Bege">Bege</option>
              <option value="Dourado">Dourado</option>
              <option value="Rosa">Rosa</option>
              <option value="Roxo">Roxo</option>
              <option value="Outro">Outro</option>
            </Select>
          </FormGroup>


          <FormGroup>
            <Label>Valor (R$) *</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="R$ 85.000,00"
              value={formData.valor}
              onChange={(e) => handleInputChange('valor', e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Quilometragem</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="15.000"
              value={formData.km}
              onChange={(e) => handleInputChange('km', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Veículo *</Label>
            <Select
              value={formData.veiculo}
              onChange={(e) => handleInputChange('veiculo', e.target.value)}
              required
            >
              <option value="">Selecione...</option>
              <option value="Carro">Carro</option>
              <option value="Caminhão">Caminhão</option>
              <option value="Caminhonete">Caminhonete</option>
              <option value="Moto">Moto</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Tipo de Veículo</Label>
            <Select
              value={formData.tipo_veiculo}
              onChange={(e) => handleInputChange('tipo_veiculo', e.target.value)}
            >
              <option value="">Selecione...</option>
              <option value="Sedan">Sedan</option>
              <option value="Hatchback">Hatchback</option>
              <option value="SUV">SUV</option>
              <option value="Pickup">Pickup</option>
              <option value="Coupé">Coupé</option>
              <option value="Conversível">Conversível</option>
              <option value="Wagon">Wagon</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Potência (CV)</Label>
            <Input
              type="number"
              inputMode="numeric"
              placeholder="140"
              min="0"
              value={formData.potencia}
              onChange={(e) => handleInputChange('potencia', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Motor</Label>
            <Input
              type="number"
              inputMode="decimal"
              placeholder="1.6"
              min="0"
              step="0.1"
              value={formData.motor}
              onChange={(e) => handleInputChange('motor', e.target.value)}
              onBlur={handleMotorBlur}
            />
          </FormGroup>

          <FormGroup>
            <Label>Tipo de Combustível</Label>
            <Select
              value={formData.combustivel}
              onChange={(e) => handleInputChange('combustivel', e.target.value)}
            >
              <option value="">Selecione o combustível</option>
              <option value="Gasolina">Gasolina</option>
              <option value="Etanol">Etanol</option>
              <option value="Flex">Flex</option>
              <option value="Diesel">Diesel</option>
              <option value="GNV">GNV</option>
              <option value="Elétrico">Elétrico</option>
              <option value="Híbrido">Híbrido</option>
              <option value="Outro">Outro</option>
            </Select>
          </FormGroup>

          {/* Mostrar campo de cliente apenas quando houver mais de um cliente disponível */}
          {clientes.length > 1 && (
            <FormGroup>
              <Label>Cliente{clientes.length > 1 ? ' *' : ''}</Label>
              <Select
                value={formData.id_cliente !== null && formData.id_cliente !== undefined ? formData.id_cliente : ''}
                onChange={(e) => handleInputChange('id_cliente', e.target.value ? parseInt(e.target.value) : null)}
                required={clientes.length > 1}
                disabled={isEditing}
                style={isEditing ? {
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'not-allowed',
                  border: '1px solid #dee2e6',
                  opacity: 1
                } : {}}
              >
                <option value="">Selecione um cliente</option>
                {clientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome_fantasia || cliente.nome_social || `Cliente ${cliente.id}`}
                  </option>
                ))}
              </Select>
              {loadingClientes && <LoadingText>Carregando clientes...</LoadingText>}
            </FormGroup>
          )}

          {isEditing && (
          <FormGroup>
              <Label>Código</Label>
            <Input
              type="text"
              value={formData.code}
                disabled={true}
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  cursor: 'not-allowed',
                  border: '1px solid #dee2e6',
                  opacity: 1
                }}
            />
          </FormGroup>
          )}
        </FormGrid>

        <FormGrid>
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="financiamento"
              checked={formData.financiamento}
              onChange={(e) => handleInputChange('financiamento', e.target.checked)}
            />
            <CheckboxLabel htmlFor="financiamento">
              Aceita financiamento
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="troca"
              checked={formData.troca}
              onChange={(e) => handleInputChange('troca', e.target.checked)}
            />
            <CheckboxLabel htmlFor="troca">
              Aceita troca
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="em_estoque"
              checked={formData.em_estoque}
              onChange={(e) => handleInputChange('em_estoque', e.target.checked)}
            />
            <CheckboxLabel htmlFor="em_estoque">
              Em estoque
            </CheckboxLabel>
          </CheckboxGroup>
        </FormGrid>

        <FormGroup>
          <Label>Descrição</Label>
          <TextArea
            placeholder="Descreva o veículo..."
            value={formData.descricao}
            onChange={(e) => handleInputChange('descricao', e.target.value)}
          />
        </FormGroup>

        <SectionSpacer />

        {formData.financiamento && (
          <FormGroup>
            <Label>Taxa de Juros (%)</Label>
            <TextArea
              placeholder="Condições de financiamento..."
              value={formData.taxa_juros}
              onChange={(e) => handleInputChange('taxa_juros', e.target.value)}
            />
          </FormGroup>
        )}

        <SectionSpacer />

        {formData.troca && (
          <FormGroup>
            <Label>Descrição da Troca</Label>
            <TextArea
              placeholder="Condições para troca..."
              value={formData.descricao_troca}
              onChange={(e) => handleInputChange('descricao_troca', e.target.value)}
            />
          </FormGroup>
        )}

        <SectionSpacer />

        <AcessoriosContainer>
          <Label>Acessórios</Label>
          {loadingAcessorios ? (
            <LoadingText>Carregando acessórios...</LoadingText>
          ) : (
            (() => {
              // Usar ordem estável (não muda quando seleção muda)
              const ordenados = acessoriosOrdenadosInicial.length > 0 
                ? acessoriosOrdenadosInicial 
                : (Array.isArray(acessorios) ? acessorios : []);
              const selectedSet = new Set(formData.acessorios || []);

              // Carrossel por páginas
              // Em mobile: 7 itens por página (1 coluna x 7 linhas)
              // Em desktop: 12 itens por página (4 colunas x 3 linhas)
              const ITEMS_PER_PAGE = isMobile ? 7 : 12;
              const pages = [];
              for (let i = 0; i < ordenados.length; i += ITEMS_PER_PAGE) {
                pages.push(ordenados.slice(i, i + ITEMS_PER_PAGE));
              }
              const totalPages = Math.max(1, pages.length);
              const page = Math.min(acessoriosPage, totalPages - 1);

              return (
                <AcessoriosCarousel>
                  <AcessoriosViewport ref={acessoriosViewportRef}>
                    <AcessoriosTrack ref={acessoriosTrackRef}>
                      {pages.map((items, idx) => {
                        const itemsToRender = [...items];
                        // Adicionar placeholders invisíveis para manter o tamanho dos cards
                        while (itemsToRender.length < ITEMS_PER_PAGE) {
                          itemsToRender.push(null);
                        }
                        return (
                          <PageGrid key={idx} $isCurrentPage={idx === page}>
                            {itemsToRender.map((acessorio, itemIdx) => 
                              acessorio ? (
                                <AcessorioButton
                                  key={acessorio.id}
                                  type="button"
                                  $selected={selectedSet.has(acessorio.id)}
                                  onClick={() => handleAcessorioToggle(acessorio.id)}
                                >
                                  {acessorio.nome}
                                </AcessorioButton>
                              ) : (
                                <PlaceholderButton key={`placeholder-${itemIdx}`} />
                              )
                            )}
                          </PageGrid>
                        );
                      })}
                    </AcessoriosTrack>

                    <ArrowButton
                      type="button"
                      $left={32}
                      disabled={page === 0}
                      onClick={() => setAcessoriosPage(p => Math.max(0, p - 1))}
                    >
                      ‹
                    </ArrowButton>
                    <ArrowButton
                      type="button"
                      $right={32}
                      disabled={page >= totalPages - 1}
                      onClick={() => setAcessoriosPage(p => Math.min(totalPages - 1, p + 1))}
                    >
                      ›
                    </ArrowButton>
                  </AcessoriosViewport>
                  
                  <AcessoriosPageIndicator>
                    {Array.from({ length: totalPages }, (_, idx) => (
                      <AcessoriosPageDot
                        key={idx}
                        $active={idx === page}
                        onClick={() => setAcessoriosPage(idx)}
                        title={`Página ${idx + 1} de ${totalPages}`}
                      />
                    ))}
                  </AcessoriosPageIndicator>
                </AcessoriosCarousel>
              );
            })()
          )}
        </AcessoriosContainer>

        <SectionSpacer />

        <ImageUploader
          label="Imagens do Veículo"
          maxSize={3 * 1024 * 1024} // 3MB
          onImagesChange={(novasImagens) => {
            setImagens(novasImagens);
            // Se não está editando, armazenar imagens pendentes
            if (!isEditing) {
              const imagensComArquivo = novasImagens.filter(img => img.file);
              setImagensPendentes(imagensComArquivo);
            }
          }}
          initialImages={imagens}
          veiculoId={veiculoId}
          idCliente={formData.id_cliente}
          onUploadSuccess={(uploadedImages) => {
            // Upload realizado
          }}
          onUploadError={(error) => {
          }}
        />

        <SectionSpacer />

        <Actions>
          <Button type="button" onClick={handleCancel}>
            Cancelar
          </Button>
          {isEditing && (
            <Button type="button" onClick={handleDeleteClick} $danger>
              <FiTrash2 />
              Excluir
            </Button>
          )}
          <Button type="submit" $success disabled={saving}>
            <FiSave />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Actions>
        </Form>
      </Container>
      
      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirmar Exclusão</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O veículo <strong>{formData.marca} {formData.modelo}</strong>
              <br />
              • Todas as imagens associadas
              <br />
              • Todos os dados relacionados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelDelete} disabled={deleting}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleDelete} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default VeiculoForm;
